import type { Pt } from "@tasduvar/engine";
import type { SheetPrim } from "./index";
import { applyTf, dimGeom, primBox, type Box } from "./edits";

/**
 * Yazı çakışması giderme (Cem 2026-09-29: "DOĞAL ZEMİN yazısı ile kot yazısı üst üste — kotu kaydırmak yanlış olur, yazıyı
 * kaydırmak daha uygun"). Üretilen pafta öğelerinde aynı blok içindeki yazı kutularını karşılaştırır; çakışan TAŞINABİLİR yazıyı
 * kendi dik yönünde (dönük çerçevede yukarı/aşağı), sonra taban çizgisi yönünde en küçük adımla boş yere kaydırır.
 * Engeller (asla taşınmaz): kot yazıları + kot üçgenleri (KOT), antet/çerçeve, tablo hücreleri (NOT), notlar (MTEXT), ölçü metinleri.
 * Saf fonksiyon: öğe sırası, anahtarı (`key`/`fp`) ve bloğu korunur; yalnız taşınan yazının `p`'si değişir.
 */
type Text = Extract<SheetPrim, { k: "text" }>;
type Dim = Extract<SheetPrim, { k: "dim" }>;
export interface LabelBox { i: number; g: string; box: Box; fixed: boolean }

const EPS = 1e-9;
const overlaps = (a: Box, b: Box): boolean => a.minX < b.maxX - EPS && b.minX < a.maxX - EPS && a.minY < b.maxY - EPS && b.minY < a.maxY - EPS;
const pad = (b: Box, d: number): Box => ({ minX: b.minX - d, minY: b.minY - d, maxX: b.maxX + d, maxY: b.maxY + d });

/** Yazı kutusu (dönüş dâhil eksene hizalı kutu; genişlik ≈ 0,6·h·karakter, düşey hizalama hesaba katılır) */
export function textBox(p: Pt, value: string, h: number, rot = 0, ha = 0, va = 0): Box {
  const w = value.replace(/%%U/g, "").length * h * 0.6, x0 = ha === 1 ? -w / 2 : ha === 2 ? -w : 0;
  const [y0, y1] = va === 2 ? [-h / 2, h / 2] : va === 3 ? [-h, 0] : va === 1 ? [0, h] : [-h * 0.25, h];
  const b: Box = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  const t = { s: 1, r: rot, tx: p.x, ty: p.y };
  for (const c of [{ x: x0, y: y0 }, { x: x0 + w, y: y0 }, { x: x0 + w, y: y1 }, { x: x0, y: y1 }]) {
    const q = applyTf(t, c);
    b.minX = Math.min(b.minX, q.x); b.minY = Math.min(b.minY, q.y); b.maxX = Math.max(b.maxX, q.x); b.maxY = Math.max(b.maxY, q.y);
  }
  return b;
}
const primTextBox = (e: Text, p: Pt = e.p): Box => textBox(p, e.value, e.opts.h, e.opts.rotation ?? 0, e.opts.halign ?? 0, e.opts.valign ?? 0);

/** Ölçü metni kutusu (SheetSvg `Dim` ile aynı yerleşim: ölçü çizgisi ortası, 0,35·th boşluk, düşeyde 90° dönük) */
function dimTextBox(e: Dim, th: number, label: string): Box {
  const { vertical, p1, p2 } = dimGeom(e), gap = th * 0.35;
  const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
  return textBox(vertical ? { x: mid.x - gap, y: mid.y } : { x: mid.x, y: mid.y + gap }, label, th, vertical ? 90 : 0, 1, 0);
}

const isFixedText = (e: Text): boolean => e.layer === "KOT" || e.layer === "NOT" || e.g === "antet" || e.g === "cerceve";

/** Paftadaki yazı benzeri kutular: yazılar, MTEXT, ölçü metinleri, kot üçgenleri (`fixed` = engel) */
export function labelBoxes(prims: SheetPrim[], th: number, dimLabel: (e: Dim) => string = (e) => (dimGeom(e).measure * 100).toFixed(0)): LabelBox[] {
  const out: LabelBox[] = [];
  prims.forEach((e, i) => {
    if (e.k === "text") out.push({ i, g: e.g, box: primTextBox(e), fixed: isFixedText(e) });
    else if (e.k === "mtext") out.push({ i, g: e.g, box: primBox(e), fixed: true });
    else if (e.k === "dim") out.push({ i, g: e.g, box: dimTextBox(e, th, e.text ?? dimLabel(e)), fixed: true });
    else if (e.k === "solid" && e.layer === "KOT") out.push({ i, g: e.g, box: primBox(e), fixed: true });
  });
  return out;
}

/** Öncelik: altı çizili başlık > balon içi poz numarası > uzun yazı (kısa etiket önce taşınır) */
const priority = (e: Text): number => (e.value.startsWith("%%U") ? 2000 : e.opts.valign === 2 ? 1000 : 0) + e.value.length;

export function declutterTexts(prims: SheetPrim[], th: number, dimLabel?: (e: Dim) => string): SheetPrim[] {
  const boxes = labelBoxes(prims, th, dimLabel);
  const out = prims.slice();
  const groups = new Map<string, LabelBox[]>();
  for (const b of boxes) { const L = groups.get(b.g) ?? []; L.push(b); groups.set(b.g, L); }
  for (const list of groups.values()) {
    const placed: Box[] = list.filter((b) => b.fixed).map((b) => b.box);
    // Önceliği yüksek olan önce yerleşir (yerinde kalır); çakışan düşük öncelikli yazı ona göre kayar
    const movable = list.filter((b) => !b.fixed).sort((a, b) => priority(prims[b.i] as Text) - priority(prims[a.i] as Text) || a.i - b.i);
    movable.forEach((m, k) => {
      if (!placed.some((q) => overlaps(m.box, q))) { placed.push(m.box); return; }
      const e = prims[m.i] as Text, h = e.opts.h, r = ((e.opts.rotation ?? 0) * Math.PI) / 180;
      const n = { x: -Math.sin(r), y: Math.cos(r) }, d = { x: Math.cos(r), y: Math.sin(r) };
      const steps: Pt[] = [];
      for (let s = 1; s * 0.6 <= 4 + EPS; s++) for (const sg of [1, -1]) steps.push({ x: n.x * sg * s * 0.6 * h, y: n.y * sg * s * 0.6 * h });
      for (let s = 1; s * 0.6 <= 4 + EPS; s++) for (const sg of [1, -1]) steps.push({ x: d.x * sg * s * 0.6 * h, y: d.y * sg * s * 0.6 * h });
      // Önce henüz yerleşmemiş yazıların asıl yerlerini de boş bırakan aday; bulunamazsa yalnız yerleşmişlere göre
      const pending = movable.slice(k + 1).map((b) => b.box);
      const fits = (bx: Box, avoid: Box[]): boolean => !avoid.some((q) => overlaps(bx, q));
      const cand = (st: Pt): { p: Pt; box: Box } => { const p = { x: e.p.x + st.x, y: e.p.y + st.y }; return { p, box: primTextBox(e, p) }; };
      const pick = steps.map(cand).find((c) => fits(pad(c.box, 0.15 * h), placed) && fits(c.box, pending))
        ?? steps.map(cand).find((c) => fits(pad(c.box, 0.15 * h), placed));
      if (pick) { out[m.i] = { ...e, p: pick.p }; placed.push(pick.box); } else placed.push(m.box);
    });
  }
  return out;
}

/** Aynı blokta çakışan yazı kutusu çiftleri (ikisi de engel olan çiftler hariç) — test ve teşhis için */
export function overlapPairs(prims: SheetPrim[], th: number, dimLabel?: (e: Dim) => string): [number, number][] {
  const L = labelBoxes(prims, th, dimLabel), pairs: [number, number][] = [];
  for (let a = 0; a < L.length; a++) for (let b = a + 1; b < L.length; b++) {
    const A = L[a]!, B = L[b]!;
    if (A.g === B.g && !(A.fixed && B.fixed) && overlaps(A.box, B.box)) pairs.push([A.i, B.i]);
  }
  return pairs;
}
